// Parses `error.stack` to find the first user frame — the function that
// actually threw, not anything inside the logger itself.
//
// Output: { function, file, line }. Any field can be undefined on engines
// whose stack format we don't recognise (older Safari, some test runners).
// We never throw from here — a stack-parse failure must not break the log call.
//
// Browser stack-trace formats handled:
//   V8 / Chrome / Edge / Node:    "    at fnName (file:line:col)"
//                                 "    at file:line:col" (anonymous)
//   Spidermonkey / Firefox:       "fnName@file:line:col"
//                                 "@file:line:col"     (anonymous)
//   JavaScriptCore / Safari:      "fnName@file:line:col"  (same as Firefox)

import type { OriginFrame } from './types'

// Frames we treat as "logger internals" — skip until we hit a real caller.
const LOGGER_INTERNALS = ['/core/logging/', 'logger.ts', 'transports.ts', 'stackTrace.ts']

const isInternalFrame = (line: string): boolean =>
  LOGGER_INTERNALS.some((needle) => line.includes(needle))

const parseV8Frame = (line: string): OriginFrame | null => {
  // "    at fnName (file:line:col)"  or  "    at file:line:col"
  const named = line.match(/^\s*at\s+(.+?)\s+\((.+?):(\d+):\d+\)\s*$/)
  if (named) {
    return { function: named[1], file: named[2], line: Number(named[3]) }
  }
  const anon = line.match(/^\s*at\s+(.+?):(\d+):\d+\s*$/)
  if (anon) {
    return { file: anon[1], line: Number(anon[2]) }
  }
  return null
}

const parseFirefoxFrame = (line: string): OriginFrame | null => {
  // "fnName@file:line:col"  or  "@file:line:col"
  const named = line.match(/^(.+?)@(.+?):(\d+):\d+$/)
  if (named) {
    const fn = named[1] ?? ''
    return {
      function: fn.length > 0 ? fn : undefined,
      file: named[2],
      line: Number(named[3]),
    }
  }
  return null
}

export const parseOriginFromStack = (stack: string | undefined): OriginFrame | undefined => {
  if (!stack) return undefined
  const lines = stack.split('\n')
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue
    if (line.startsWith('Error')) continue // V8 header
    if (isInternalFrame(line)) continue

    const v8 = parseV8Frame(line)
    if (v8) return v8

    const ff = parseFirefoxFrame(line)
    if (ff) return ff
  }
  return undefined
}
